/**
 * Address matching utilities for MapView entity selection
 *
 * This module provides address normalization and matching algorithms
 * to improve entity selection accuracy over proximity-based matching.
 *
 * The EntityLocationDTO now supports an optional formattedAddress property
 * from the server SearchArea response. When available, this is used for
 * address-based matching instead of relying solely on proximity.
 */

import {
  EntityLocationDTO,
  MultiTenantLocationDetails,
} from '../store/map/typings'
import { toast } from '@sales-center/components'

/**
 * Normalizes an address string for comparison by:
 * - Converting to lowercase
 * - Removing punctuation and special characters
 * - Normalizing whitespace
 * - Filtering out common stopwords
 */
export function normalizeAddress(address: string): string {
  if (!address) return ''

  return address
    .toLowerCase()
    .replace(/[^\w\s]/g, '') // Remove punctuation
    .replace(/\b(apt|apartment|suite|ste|unit|#)\b/g, '') // Remove apartment indicators
    .replace(
      /\b(street|st|avenue|ave|road|rd|drive|dr|lane|ln|boulevard|blvd|way|court|ct)\b/g,
      '',
    ) // Remove street type abbreviations
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim()
}

/**
 * Extracts the leading house number from a normalized address string, if present.
 */
function extractHouseNumber(normalizedAddress: string): string | null {
  const match = normalizedAddress.match(/^(\d+)\b/)
  return match ? match[1] : null
}

/**
 * Calculates a similarity score between two normalized addresses
 * Returns a value between 0 and 1, where 1 is an exact match
 */
export function calculateAddressSimilarity(
  address1: string,
  address2: string,
): number {
  const normalized1 = normalizeAddress(address1)
  const normalized2 = normalizeAddress(address2)

  if (!normalized1 && !normalized2) return 0
  if (normalized1 === normalized2) return 1

  // Two different house numbers can never be the same physical address,
  // no matter how similar the rest of the address text is — without this
  // gate, addresses that share a street/city/zip but differ only by house
  // number still score at or above the match threshold, because the
  // shared words outweigh the one differing word (SH-1325).
  const houseNumber1 = extractHouseNumber(normalized1)
  const houseNumber2 = extractHouseNumber(normalized2)
  if (houseNumber1 && houseNumber2 && houseNumber1 !== houseNumber2) {
    return 0
  }

  const words1 = normalized1.split(/\s+/).filter((word) => word.length > 2)
  const words2 = normalized2.split(/\s+/).filter((word) => word.length > 2)

  if (words1.length === 0 && words2.length === 0) return 0
  if (words1.length === 0 || words2.length === 0) return 0

  // Count matching words (partial matches allowed)
  const matchingWords = words1.filter((word1) =>
    words2.some(
      (word2) =>
        word1.includes(word2) ||
        word2.includes(word1) ||
        levenshteinDistance(word1, word2) <= 1,
    ),
  )

  // Calculate similarity based on matched words vs total unique words
  const totalUniqueWords = new Set([...words1, ...words2]).size
  return matchingWords.length / totalUniqueWords
}

/**
 * Simple Levenshtein distance calculation for fuzzy word matching
 */
function levenshteinDistance(str1: string, str2: string): number {
  const matrix = Array.from({ length: str2.length + 1 }, (_, i) => [i])
  matrix[0] = Array.from({ length: str1.length + 1 }, (_, i) => i)

  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2[i - 1] === str1[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1]
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1, // deletion
          matrix[i][j - 1] + 1, // insertion
          matrix[i - 1][j - 1] + 1, // substitution
        )
      }
    }
  }

  return matrix[str2.length][str1.length]
}

/**
 * Extracts address components from EntityLocationDTO when available
 * Uses formattedAddress if present, otherwise assembles from the component
 * fields the server sends (street/houseNumber/city/state/postalCode).
 */
export function extractEntityAddress(entity: EntityLocationDTO): string {
  if (entity.formattedAddress) {
    return entity.formattedAddress
  }

  const streetLine = [entity.houseNumber, entity.street || entity.street1]
    .filter((p): p is string => !!p && p.trim().length > 0)
    .join(' ')

  const parts = [
    streetLine,
    entity.street2,
    entity.city,
    entity.stateAbbreviation,
    entity.postalCode,
  ].filter((p): p is string => !!p && p.trim().length > 0)

  return parts.join(' ')
}

/**
 * Extracts address components from MultiTenantLocationDetails when available
 * This handles cases where we have detailed location information
 */
export function extractDetailedEntityAddress(
  location: MultiTenantLocationDetails,
): string {
  const parts = [
    location.street1,
    location.street2,
    location.city,
    location.stateAbbreviation,
    location.postalCode,
  ].filter((part) => part && part.trim())

  return parts.join(' ')
}

/**
 * Finds the best address match from a list of entities
 * Returns the entity with the highest address similarity score above the threshold
 */
export function findBestAddressMatch<T extends { address?: string }>(
  targetAddress: string,
  entities: T[],
  threshold: number = 0.6,
): { entity: T; score: number } | null {
  if (!targetAddress || entities.length === 0) return null

  let bestMatch: T | null = null
  let bestScore = 0

  for (const entity of entities) {
    if (!entity.address) continue

    const score = calculateAddressSimilarity(targetAddress, entity.address)
    if (score > bestScore && score >= threshold) {
      bestScore = score
      bestMatch = entity
    }
  }

  return bestMatch ? { entity: bestMatch, score: bestScore } : null
}

/**
 * Main address matching function for EntityLocationDTO array
 * Uses formattedAddress when available, falls back to proximity matching
 */
export function matchEntityByAddress(
  targetAddress: string,
  entities: EntityLocationDTO[],
  fallbackToProximity: boolean = true,
  targetLat?: number,
  targetLng?: number,
): { entity: EntityLocationDTO; matchType: 'address' | 'proximity' } | null {
  // Try address matching first if we have a target address
  if (targetAddress && targetAddress.trim()) {
    let bestMatch: EntityLocationDTO | null = null
    let bestScore = 0
    const threshold = 0.6

    for (const entity of entities) {
      const entityAddress = extractEntityAddress(entity)
      if (!entityAddress) continue

      const score = calculateAddressSimilarity(targetAddress, entityAddress)
      if (score > bestScore && score >= threshold) {
        bestScore = score
        bestMatch = entity
      }
    }

    if (bestMatch) {
      console.log(
        'Found address match with score:',
        bestScore,
        'for:',
        targetAddress,
      )
      return { entity: bestMatch, matchType: 'address' }
    }
  }

  // Fallback to proximity matching if address matching didn't work
  if (
    fallbackToProximity &&
    targetLat !== undefined &&
    targetLng !== undefined
  ) {
    console.log('Using proximity matching fallback for address:', targetAddress)

    let nearestEntity: EntityLocationDTO | null = null
    let minDistance = Infinity
    // ~0.001 degrees ≈ 100m at mid-latitudes. The previous 0.0002 (~17m) was
    // tighter than the typical drift between HERE's geocoded centroid and an
    // entity's stored coordinates, so search results sat right next to a pin
    // without auto-selecting it.
    const distanceThreshold = 0.0004

    for (const entity of entities) {
      const distance = Math.sqrt(
        Math.pow(targetLat - entity.latitude, 2) +
          Math.pow(targetLng - entity.longitude, 2),
      )

      if (distance < minDistance) {
        minDistance = distance
        nearestEntity = entity
      }
    }

    if (nearestEntity && minDistance < distanceThreshold) {
      toast.info(
        'We couldn’t find the address you are looking for, so we’re displaying the closest most relevant address instead.',
      )
      return { entity: nearestEntity, matchType: 'proximity' }
    }
  }

  return null
}
